import * as React from 'react';
import { RouteComponentProps } from 'react-router';
// import { Form, ControlProps } from 'react-redux-form';

// Models
import { CustomerViewModel } from '@models/Customer';
import { ContractViewModel, SimpleEntityViewModel, ContractOrderType } from '@models/Contract';
import { OptionsViewModel } from '@models/common.models';
import { RealEstateAgentViewModel } from '@models/RealEstateAgent';
import { RealEstateOfficeViewModel, RealEstateTerritoryViewModel } from '@models/RealEstateOffice';
import { EscrowOfficerViewModel } from '@models/EscrowOfficer';
import { EscrowOfficeViewModel } from '@models/EscrowOffice';
import { TransactionCoordinatorViewModel } from '@models/TransactionCoordinator';

// Form Validation
// import { hasValue, getDefaultErrorMessages } from '@common/validators';

// Components
import { ModalProps } from '@common/components/modal';
import { ConfirmSaveComponent } from './confirm-save.component';
import { ContractInfoComponent } from '../contract-info/contract-info.component';

import * as classes from './summary.scss';
import { PropertyManagerSearchViewModel } from '@models/PropertyManager';
import { Card, CardHeader, CardBody, SpinnerPage } from '@common/components';
import { CommentType } from '@models/Comment';
import { Comment } from 'src/comments';

export interface SummaryProperties extends RouteComponentProps<{ id?: number }> {
  readonly message: string;
  readonly currentStep: number;
  readonly contractVM: ContractViewModel | null;

  readonly tempComment?: any;
  readonly tempUsers?: any;

  readonly additionalRealEstateAgents: Array<RealEstateAgentViewModel>;
  readonly contactInitiators: Array<OptionsViewModel>;
  readonly contactMediums: Array<OptionsViewModel>;
  readonly contactTypes: Array<OptionsViewModel>;
  readonly propertyTypes: Array<OptionsViewModel>;
  readonly propertySaleTypes: Array<OptionsViewModel>;

  readonly billingOptions: Array<OptionsViewModel>;
  readonly coverageOptions: Array<OptionsViewModel>;
  readonly assetManagers: Array<OptionsViewModel>;
  readonly buyerCustomer: CustomerViewModel;
  readonly sellerCustomer: CustomerViewModel;
  readonly propertyManager: PropertyManagerSearchViewModel;
  readonly initAgent: RealEstateAgentViewModel;
  readonly coopAgent: RealEstateAgentViewModel;
  readonly initOffice: RealEstateOfficeViewModel;
  readonly coopOffice: RealEstateOfficeViewModel;
  readonly initSam: RealEstateTerritoryViewModel;
  readonly coopSam: RealEstateTerritoryViewModel;
  readonly totalPrice: number;
  readonly escrowOfficer: EscrowOfficerViewModel;
  readonly escrowOffice: EscrowOfficeViewModel;
  readonly transactionCoordinator: Array<TransactionCoordinatorViewModel>;
  readonly jump: (step: number) => void | undefined;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly onSaveClick: (
    contractVM: ContractViewModel | null,
    isRenewal?: boolean,
    tempComment?: string,
    tempUsers?: OptionsViewModel[]
  ) => any;
  readonly toggleModalAndValidate: (modalProps?: ModalProps, tempComment?: string) => void;
  readonly toggleModal: (modalProps?: ModalProps) => void;
  readonly isRenewal?: boolean;
  readonly totalAdjustment: number;
  readonly orderType?: ContractOrderType;
}

export class SummaryComponent extends React.Component<SummaryProperties> {
  constructor(props: SummaryProperties) {
    super(props);

    this.showConfirmationModal = this.showConfirmationModal.bind(this);
  }

  renderError() {
    if (!this.props.message) {
      return;
    }

    return <div className="error">{this.props.message}</div>;
  }

  showConfirmationModal(event?: any) {
    event.preventDefault();
    event.stopPropagation();
    if (this.props.isRenewal) {
      this.props.onSaveClick(this.props.contractVM, this.props.isRenewal, this.props.tempComment, this.props.tempUsers);
    } else {
      this.props.toggleModalAndValidate(
        {
          title: 'Save Contract',
          closeLabel: 'Cancel and Edit More',
          saveLabel: 'Save And Continue to Payment',
          component: ConfirmSaveComponent,
          saveCallback: this.handleSubmit,
          isSmall: true
        },
        this.props.tempComment
      );
    }
  }

  handleSubmit = () => {
    this.props.toggleModal();
    this.props.onSaveClick(this.props.contractVM, this.props.isRenewal, this.props.tempComment, this.props.tempUsers);
  };

  renderContractInfo() {
    let contractVM = this.props.contractVM;
    if (!contractVM) {
      return;
    }
    let txCoords = (contractVM.transactionCoordinatorIds ? contractVM.transactionCoordinatorIds : []).map(tc => {
      return (
        tc &&
        ({
          id: tc.id,
          name: tc.name,
          email: tc.emailAddress,
          phone: tc.directPhone,
          fax: tc.directFax
        } as SimpleEntityViewModel)
      );
    });
    let reAgents = this.props.additionalRealEstateAgents.map(ra => {
      return (
        ra &&
        ({
          id: ra.id,
          name: ra.name,
          email: ra.emailAddress,
          phone: ra.directPhone,
          fax: ra.directFax
        } as SimpleEntityViewModel)
      );
    });

    let productGroup = contractVM.productGroup;
    let product = contractVM.product;

    return (
      <ContractInfoComponent
        {...this.props}
        id={contractVM && contractVM.id ? contractVM.id : undefined}
        contactName={contractVM.contactName}
        contactType={contractVM.contactType}
        contactMedium={contractVM.contactMedium}
        contactInit={contractVM.contactInit}
        product={product}
        coverageOptions={contractVM.coverageOptions}
        productGroup={productGroup}
        priceAdjustment={contractVM.priceAdjustment}
        priceAdjustmentAmount={contractVM.priceAdjustmentAmount}
        employeeDiscount={contractVM.employeeDiscount}
        totalPrice={contractVM.totalPrice}
        originalPrice={contractVM.totalPrice + contractVM.priceAdjustmentAmount}
        propertyAddress={contractVM.propertyAddress}
        propertyType={contractVM.propertyType}
        yearBuilt={contractVM.yearBuilt}
        propertySaleType={contractVM.propertySaleType}
        assetManager={contractVM.assetManager}
        transCoordinator={undefined}
        additionalTxCoordinators={txCoords}
        additionalREAgents={reAgents}
        onBuyerCustomerSelect={null}
        onSellerCustomerSelect={null}
        toggleModal={null}
        jump={this.props.currentStep === 0 ? undefined : this.props.jump}
        contractNote={contractVM.contractNote}
        totalAdjustment={this.props.totalAdjustment}
        noRenew={contractVM.noRenew}
        noRenewReasons={contractVM.noRenewReasons}
        coeDate={contractVM.estimatedCoeDate}
        orderType={this.props.orderType}
      />
    );
  }

  renderCommentBox() {
    return (
      <div className="row p-10">
        <div className="col-md-12">
          <Card>
            <CardHeader>
              <div className="row m-3">
                <div className="col-xs-12">
                  <h3>Comment</h3>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <div className="row">
                <div className="col-md-12">
                  <Comment entityId={-1} commentType={CommentType.Contract} hideSave={true} />
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  render() {
    return this.props.message === 'Saving Contract Start' ? (
      <SpinnerPage />
    ) : (
      <>
        <section className={classes.summary}>
          {this.renderContractInfo()}
          {this.renderCommentBox()}
          <form onSubmit={this.showConfirmationModal}>
            <div className="row end-xs">
              <div className="col-md">
                <button hidden={true} className="btn btn-primary" type="submit" ref={this.props.buttonRef}>
                  Continue
                </button>
              </div>
            </div>
          </form>
        </section>
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/contract/_components/summary/summary.component.tsx